import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { parseFilter, parseLimit, toSearchParams } from './utils';

import { ProviderTransactionFilter } from 'v2/types/ProviderTransactionModel';

interface UseProviderTransactionsFilter {
  filter: ProviderTransactionFilter;
  requestFilter: ProviderTransactionFilter;
  limit: number;
  onChange: (key: keyof ProviderTransactionFilter, value: string) => void;
  onPatch: (patch: Partial<ProviderTransactionFilter>) => void;
  onLimitChange: (limit: number) => void;
}

// Filter state is restored from the query string once and then mirrored back into it,
// so a selection can be shared by link and opened from the user profile.
export function useProviderTransactionsFilter(): UseProviderTransactionsFilter {
  const [searchParams, setSearchParams] = useSearchParams();

  const [initialFilter] = useState(() => parseFilter(searchParams));
  const [limit, setLimit] = useState(() => parseLimit(searchParams.get('limit')));

  const [filter, setFilter] = useState<ProviderTransactionFilter>(initialFilter);

  const onChange = useCallback((key: keyof ProviderTransactionFilter, value: string) => {
    setFilter((prevFilter) => ({ ...prevFilter, [key]: value }));
  }, []);

  const onPatch = useCallback((patch: Partial<ProviderTransactionFilter>) => {
    setFilter((prevFilter) => ({ ...prevFilter, ...patch }));
  }, []);

  useEffect(() => {
    const nextSearchParams = toSearchParams(filter, limit);

    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [filter, limit, searchParams, setSearchParams]);

  // All filters are pickers and selects now, nothing needs debouncing: the request filter is the filter itself.
  return { filter, requestFilter: filter, limit, onChange, onPatch, onLimitChange: setLimit };
}
